import { memo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { StyleProp, ViewStyle } from 'react-native';

import { MediaImageSized } from '@/features/media/components/MediaImageSized';
import { useDetailImageUpload } from '@/features/profile-setting/components/organization-edit/DetailImageUploadContext';
import type { DetailImageEntry } from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type Props = {
  entry: DetailImageEntry;
  index: number;
  total: number;
  /** Per-index field-array error message, if validation failed. */
  errorMessage?: string;
  onRemove: (index: number) => void;
  removeDisabled?: boolean;
  /** Drag-handle node (a `Sortable.Handle`) shown when sorting is enabled. */
  dragHandle?: ReactNode;
  /** Explicit tile size; required inside the sortable grid so cells don't
   * collapse (MediaImageSized defaults to percentage sizing otherwise). */
  style?: StyleProp<ViewStyle>;
};

/**
 * Form variant of MediaImageSized: wires the detailImages field-array
 * entry, its per-index error state and the localized labels.
 *
 * Memoized: the sortable grid re-renders the list on every drag frame, so
 * skipping unchanged tiles keeps dragging smooth.
 */
export const FormImageThumbnail = memo<Props>(function FormImageThumbnail({
  entry,
  index,
  total,
  errorMessage,
  onRemove,
  removeDisabled,
  dragHandle,
  style
}) {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { uploadStates } = useDetailImageUpload();
  const uploadState = uploadStates.get(entry.key);

  const imageLabel = t('fields.imageLabel', { index: index + 1, total });

  return (
    <MediaImageSized
      mediaItem={entry.mediaItem}
      localImageUri={entry.asset?.uri}
      isLoading={uploadState === 'uploading'}
      accessibilityLabel={
        imageLabel + (errorMessage ? `. ${errorMessage}` : '')
      }
      accessibilityHint={imageLabel}
      hasError={!!errorMessage || uploadState === 'error'}
      onRemove={() => onRemove(index)}
      removeLabel={t('actions.removeImageWithIndex', { index: index + 1 })}
      removeDisabled={removeDisabled}
      dragHandle={dragHandle}
      style={style}
    />
  );
});
